import { motion } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import {
  Check,
  ArrowRight,
  Users,
  Briefcase,
  Activity,
  Stethoscope,
  Video,
  Shield,
  Star,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../PageRouting/pageRoutes";

const THEME_CSS = `
  #providers-section {
    background: #f8fafc;
  }
  #providers-section .dox-card-provider {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    box-shadow: 0 2px 12px rgba(3,45,87,0.06);
  }
  #providers-section .dox-card-patient {
    background: linear-gradient(135deg, #0e1f35 0%, #122d48 100%);
    border: 1px solid rgba(255,255,255,0.08);
  }
  #providers-section .dox-section-heading   { color: #0f172a; }
  #providers-section .dox-provider-heading  { color: #0f172a; }
  #providers-section .dox-provider-sub      { color: #64748b; }
  #providers-section .dox-provider-benefit  { color: #334155; }
  #providers-section .dox-patient-heading   { color: #ffffff; }
  #providers-section .dox-patient-sub       { color: rgba(255,255,255,0.65); }
  #providers-section .dox-patient-benefit   { color: rgba(255,255,255,0.85); }
  #providers-section .dox-badge-provider {
    background: rgba(20,184,166,0.10);
    border: 1px solid rgba(20,184,166,0.25);
    color: #0f766e;
  }
  #providers-section .dox-badge-patient {
    background: rgba(255,255,255,0.10);
    border: 1px solid rgba(255,255,255,0.15);
    color: #5eead4;
  }
  #providers-section .dox-check-provider {
    background: #0f172a;
    border: none;
  }
  #providers-section .dox-check-provider svg { color: #ffffff; }
  #providers-section .dox-check-patient {
    background: #2dd4bf;
    border: none;
  }
  #providers-section .dox-check-patient svg { color: #0f172a; }
  #providers-section .dox-btn-provider {
    background: #0f172a;
    color: #ffffff;
  }
  #providers-section .dox-btn-provider:hover { background: #1e293b; }
  #providers-section .dox-btn-patient {
    background: #ffffff;
    color: #0f172a;
  }
  #providers-section .dox-btn-patient:hover  { background: #f1f5f9; }
  #providers-section .dox-link-provider { color: #475569; }
  #providers-section .dox-link-provider:hover { color: #0d9488; }
  #providers-section .dox-link-patient  { color: rgba(255,255,255,0.75); }
  #providers-section .dox-link-patient:hover  { color: #ffffff; }
  #providers-section .dox-glow-provider {
    background: rgba(20,184,166,0.10);
  }
  #providers-section .dox-network {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.10);
  }
  #providers-section .dox-network-glow1 { background: rgba(45,212,191,0.25); }
  #providers-section .dox-network-glow2 { background: rgba(34,211,238,0.20); }
  #providers-section .dox-activity-icon { background: rgba(45,212,191,0.20); color: #2dd4bf; }
  #providers-section .dox-ping          { background: #2dd4bf; }
  #providers-section .dox-live-label    { color: rgba(255,255,255,0.80); }
  #providers-section .dox-region-badge  {
    background: rgba(255,255,255,0.10);
    border: 1px solid rgba(255,255,255,0.15);
    color: rgba(255,255,255,0.70);
  }
  #providers-section .dox-stat-card {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.10);
  }
  #providers-section .dox-stat-icon  { color: #5eead4; }
  #providers-section .dox-stat-value { color: #ffffff; }
  #providers-section .dox-stat-label { color: rgba(255,255,255,0.55); }
  #providers-section .dox-avatar-row {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.10);
  }
  #providers-section .dox-avatar-border { border-color: rgba(14,31,53,0.80); }
  #providers-section .dox-avatar-border.dox-avatar-0 { background: linear-gradient(135deg, #2dd4bf, #06b6d4) !important; color: #0f172a !important; }
  #providers-section .dox-avatar-border.dox-avatar-1 { background: linear-gradient(135deg, #06b6d4, #2dd4bf) !important; color: #0f172a !important; }
  #providers-section .dox-avatar-border.dox-avatar-2 { background: linear-gradient(135deg, #5eead4, #22d3ee) !important; color: #0f172a !important; }
  #providers-section .dox-avatar-border.dox-avatar-3 { background: linear-gradient(135deg, #67e8f9, #5eead4) !important; color: #0f172a !important; }
  #providers-section .dox-avatar-more  {
    background: rgba(255,255,255,0.15);
    border-color: rgba(14,31,53,0.80);
    color: #ffffff;
  }
  #providers-section .dox-stars        { color: #5eead4; }
  #providers-section .dox-review-text  { color: rgba(255,255,255,0.60); }

  [data-theme="dark"] #providers-section {
    background: #0a0800;
  }
  [data-theme="dark"] #providers-section .dox-card-provider {
    background: #110d02;
    border: 1px solid rgba(200,150,30,0.30);
    box-shadow: 0 4px 40px rgba(200,150,30,0.07);
  }
  [data-theme="dark"] #providers-section .dox-card-patient {
    background: linear-gradient(135deg, #1a1200 0%, #0f0a00 100%);
    border: 1px solid rgba(200,150,30,0.40);
    box-shadow: 0 8px 48px rgba(200,150,30,0.10);
  }
  [data-theme="dark"] #providers-section .dox-provider-heading { color: #f5e6b0; }
  [data-theme="dark"] #providers-section .dox-provider-sub     { color: #b89a4a; }
  [data-theme="dark"] #providers-section .dox-provider-benefit { color: #e0c880; }
  [data-theme="dark"] #providers-section .dox-patient-heading  { color: #f5e6b0; }
  [data-theme="dark"] #providers-section .dox-patient-sub      { color: #b89a4a; }
  [data-theme="dark"] #providers-section .dox-patient-benefit  { color: #e0c880; }
  [data-theme="dark"] #providers-section .dox-badge-provider {
    background: rgba(200,150,30,0.12);
    border: 1px solid rgba(200,150,30,0.35);
    color: #f0c040;
  }
  [data-theme="dark"] #providers-section .dox-badge-patient {
    background: rgba(200,150,30,0.10);
    border: 1px solid rgba(200,150,30,0.28);
    color: #c8961e;
  }
  [data-theme="dark"] #providers-section .dox-check-provider {
    background: rgba(200,150,30,0.15);
    border: 1px solid rgba(200,150,30,0.45);
  }
  [data-theme="dark"] #providers-section .dox-check-provider svg { color: #f0c040; }
  [data-theme="dark"] #providers-section .dox-check-patient {
    background: #c8961e;
    border: none;
  }
  [data-theme="dark"] #providers-section .dox-check-patient svg { color: #0a0800; }
  [data-theme="dark"] #providers-section .dox-btn-provider {
    background: #c8961e;
    color: #0a0800;
  }
  [data-theme="dark"] #providers-section .dox-btn-provider:hover { background: #f0c040; }
  [data-theme="dark"] #providers-section .dox-btn-patient {
    background: #c8961e;
    color: #0a0800;
  }
  [data-theme="dark"] #providers-section .dox-btn-patient:hover  { background: #f0c040; }
  [data-theme="dark"] #providers-section .dox-link-provider { color: #b89a4a; }
  [data-theme="dark"] #providers-section .dox-link-provider:hover { color: #f0c040; }
  [data-theme="dark"] #providers-section .dox-link-patient  { color: #b89a4a; }
  [data-theme="dark"] #providers-section .dox-link-patient:hover  { color: #f0c040; }
  [data-theme="dark"] #providers-section .dox-glow-provider {
    background: rgba(200,150,30,0.07);
  }
  [data-theme="dark"] #providers-section .dox-network {
    background: rgba(200,150,30,0.05);
    border: 1px solid rgba(200,150,30,0.18);
  }
  [data-theme="dark"] #providers-section .dox-network-glow1 { background: rgba(200,150,30,0.12); }
  [data-theme="dark"] #providers-section .dox-network-glow2 { background: rgba(240,192,64,0.08); }
  [data-theme="dark"] #providers-section .dox-activity-icon { background: rgba(200,150,30,0.18); color: #f0c040; }
  [data-theme="dark"] #providers-section .dox-ping          { background: #f0c040; }
  [data-theme="dark"] #providers-section .dox-live-label    { color: #e0c880; }
  [data-theme="dark"] #providers-section .dox-region-badge  {
    background: rgba(200,150,30,0.10);
    border: 1px solid rgba(200,150,30,0.20);
    color: #b89a4a;
  }
  [data-theme="dark"] #providers-section .dox-stat-card {
    background: rgba(200,150,30,0.05);
    border: 1px solid rgba(200,150,30,0.14);
  }
  [data-theme="dark"] #providers-section .dox-stat-icon  { color: #c8961e; }
  [data-theme="dark"] #providers-section .dox-stat-value { color: #f5e6b0; }
  [data-theme="dark"] #providers-section .dox-stat-label { color: #7a5c10; }
  [data-theme="dark"] #providers-section .dox-avatar-row {
    background: rgba(200,150,30,0.04);
    border: 1px solid rgba(200,150,30,0.12);
  }
  [data-theme="dark"] #providers-section .dox-avatar-border { border-color: #0a0800; }
  [data-theme="dark"] #providers-section .dox-avatar-border.dox-avatar-0 { background: linear-gradient(135deg, #f0c040, #c8961e) !important; color: #0a0800 !important; }
  [data-theme="dark"] #providers-section .dox-avatar-border.dox-avatar-1 { background: linear-gradient(135deg, #e0b030, #a07010) !important; color: #0a0800 !important; }
  [data-theme="dark"] #providers-section .dox-avatar-border.dox-avatar-2 { background: linear-gradient(135deg, #d4a028, #8a6010) !important; color: #0a0800 !important; }
  [data-theme="dark"] #providers-section .dox-avatar-border.dox-avatar-3 { background: linear-gradient(135deg, #c89020, #7a5010) !important; color: #0a0800 !important; }
  [data-theme="dark"] #providers-section .dox-avatar-more  {
    background: rgba(200,150,30,0.15);
    border-color: #0a0800;
    color: #c8961e;
  }
  [data-theme="dark"] #providers-section .dox-stars       { color: #f0c040; }
  [data-theme="dark"] #providers-section .dox-review-text { color: #7a5c10; }

  /* ── Network card mobile fixes ── */
  @media (max-width: 640px) {
    #providers-section .dox-network {
      padding: 14px !important;
      margin-top: 20px !important;
    }
    #providers-section .dox-stat-card {
      padding: 8px 4px !important;
      border-radius: 10px !important;
    }
    #providers-section .dox-stat-value {
      font-size: 1rem !important;
      margin-top: 6px !important;
    }
    #providers-section .dox-stat-label {
      font-size: 9px !important;
      letter-spacing: 0.04em !important;
    }
    #providers-section .dox-avatar-row {
      padding: 8px 10px !important;
      gap: 6px !important;
    }
    #providers-section .dox-live-label {
      font-size: 10px !important;
    }
  }
`;

if (typeof document !== "undefined" && !document.getElementById("dox-pp-theme")) {
  const tag = document.createElement("style");
  tag.id = "dox-pp-theme";
  tag.textContent = THEME_CSS;
  document.head.appendChild(tag);
}

const PROVIDER_BENEFITS = [
  "Reduce no-shows by up to 38% with smart reminders",
  "All-in-one EHR, billing and inventory in one place",
  "Virtual and In clinic appointment management",
  "Custom branded patient portal & mobile app",
];

const PATIENT_BENEFITS = [
  "Virtual consultation with trusted doctors",
  "Secure messaging, prescriptions & lab results",
  "Family profiles and care plans in one app",
  "Transparent pricing — pay with insurance or card",
];

const NETWORK_STATS = [
  { Icon: Stethoscope, value: "12K+", label: "Doctors" },
  { Icon: Video,       value: "98%",  label: "Match rate" },
  { Icon: Shield,      value: "<60s", label: "To book" },
];

const AVATARS = ["DR", "MS", "JK", "AL"];

function NetworkWidget() {
  const ref = useRef(null);
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setNarrow(entry.contentRect.width < 400);
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="dox-network relative mt-8 overflow-hidden rounded-2xl transition-all duration-300"
      style={{ padding: narrow ? "12px" : "20px", backdropFilter: "blur(8px)" }}
    >
      <div className="dox-network-glow1 pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full" style={{ filter: "blur(48px)" }} />
      <div className="dox-network-glow2 pointer-events-none absolute -bottom-16 -left-10 h-40 w-40 rounded-full" style={{ filter: "blur(48px)" }} />

      {/* Header */}
      <div className="relative flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 shrink-0">
          <span className="dox-activity-icon relative grid h-7 w-7 place-items-center rounded-lg transition-all duration-300 shrink-0">
            <Activity className="h-3.5 w-3.5" />
            <span className="dox-ping absolute right-0.5 top-0.5 h-1.5 w-1.5 animate-ping rounded-full" />
            <span className="dox-ping absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full" />
          </span>
          <p className="dox-live-label font-semibold uppercase tracking-wider transition-colors duration-300 whitespace-nowrap" style={{ fontSize: "10px" }}>
            Live network
          </p>
        </div>
        <span className="dox-region-badge rounded-full font-semibold transition-all duration-300 whitespace-nowrap shrink-0" style={{ padding: "2px 8px", fontSize: "9px" }}>
          UK · US · EU
        </span>
      </div>

      {/* Stats — always 3 col but compact */}
      <div className="relative mt-4 grid grid-cols-3 text-center" style={{ gap: narrow ? "6px" : "10px" }}>
        {NETWORK_STATS.map(({ Icon, value, label }, i) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 + i * 0.08 }}
            className="dox-stat-card rounded-xl transition-all duration-300"
            style={{ padding: narrow ? "8px 4px" : "12px 8px" }}
          >
            <Icon className="dox-stat-icon mx-auto h-3.5 w-3.5" />
            <p className="dox-stat-value font-bold" style={{ fontSize: narrow ? "0.85rem" : "1.1rem", marginTop: "6px" }}>{value}</p>
            <p className="dox-stat-label uppercase" style={{ fontSize: "8px", letterSpacing: "0.04em", marginTop: "2px" }}>{label}</p>
          </motion.div>
        ))}
      </div>

      {/* Avatar row */}
      <div
        className="dox-avatar-row relative mt-4 rounded-xl transition-all duration-300"
        style={{
          padding: narrow ? "8px 10px" : "10px 14px",
          display: "flex",
          flexDirection: narrow ? "column" : "row",
          alignItems: narrow ? "flex-start" : "center",
          justifyContent: "space-between",
          gap: narrow ? "8px" : "0",
        }}
      >
        <div className="flex items-center -space-x-1.5">
          {AVATARS.map((initials, i) => (
            <span
              key={i}
              className={`dox-avatar-border dox-avatar-${i} grid place-items-center rounded-full border-2 font-bold transition-all duration-300`}
              style={{ width: "28px", height: "28px", fontSize: "9px" }}
            >
              {initials}
            </span>
          ))}
          <span
            className="dox-avatar-more grid place-items-center rounded-full border-2 font-bold transition-all duration-300"
            style={{ marginLeft: "8px", minWidth: "30px", height: "28px", padding: "0 6px", fontSize: "9px" }}
          >
            +8K
          </span>
        </div>
        <div className={narrow ? "flex items-center gap-2" : "text-right"}>
          <div className="dox-stars flex items-center gap-0.5 transition-colors duration-300">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="h-2.5 w-2.5 fill-current" />
            ))}
          </div>
          <p className="dox-review-text text-[9px] transition-colors duration-300" style={{ marginTop: narrow ? "0" : "2px", whiteSpace: "nowrap" }}>
            4.9 · 21,840 reviews
          </p>
        </div>
      </div>
    </div>
  );
}

export function ProvidersPatients() {
  const navigate = useNavigate();
  return (
    <section id="providers-section" className="relative py-16 sm:py-24 lg:py-32">

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        {/* ── SINGLE column on mobile, TWO columns on md+ ── */}
        <div className="grid gap-6 sm:grid-cols-1 grid-cols-2">

          {/* ── FOR PROVIDERS ── */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="dox-card-provider relative overflow-hidden rounded-[2rem] p-6 sm:p-8 lg:p-12 transition-all duration-300"
          >
            <span className="dox-badge-provider inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider transition-all duration-300">
              <Briefcase className="h-3.5 w-3.5" />
              Providers
            </span>

            <h3 className="dox-provider-heading mt-5 font-sans text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight transition-colors duration-300">
              Built your smart clinic.
            </h3>

            <p className="dox-provider-sub mt-3 text-sm leading-relaxed transition-colors duration-300">
              Doxzon helps healthcare providers manage consultation, patient records, diagnostics, appointments, and home care visit services — all from one secure platform.
            </p>

            <ul className="mt-7 space-y-3">
              {PROVIDER_BENEFITS.map((benefit) => (
                <li key={benefit} className="dox-provider-benefit flex items-start gap-3 text-sm transition-colors duration-300">
                  <span className="dox-check-provider mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full transition-all duration-300">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  {benefit}
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={() => navigate(APP_ROUTES.BOOKDEMO)}
                className="dox-btn-provider border-none inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer"
              >
                Request a demo <ArrowRight className="h-4 w-4" />
              </button>
              <a href="#features" className="dox-link-provider text-sm font-semibold transition-colors duration-200">
                Explore providers solution →
              </a>
            </div>

            <div
              className="dox-glow-provider pointer-events-none absolute -bottom-14 -right-14 h-56 w-56 rounded-full transition-all duration-300"
              style={{ filter: "blur(48px)" }}
            />
          </motion.div>

          {/* ── FOR PATIENTS ── */}
          <motion.div
            id="patients-section"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="dox-card-patient relative overflow-hidden rounded-[2rem] p-6 sm:p-8 lg:p-12 transition-all duration-300"
          >
            <span className="dox-badge-patient inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider transition-all duration-300">
              <Users className="h-3.5 w-3.5" />
              Patients
            </span>

            <h3 className="dox-patient-heading mt-5 font-sans text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight transition-colors duration-300">
              Smarter healthcare starts here.
            </h3>

            <p className="dox-patient-sub mt-3 text-sm leading-relaxed transition-colors duration-300">
              Secure virtual consultations with trusted doctors. Fast, convenient, and seamlessly connected to your digital health records. Your complete journey, connected in one place.
            </p>

            <ul className="mt-7 space-y-3">
              {PATIENT_BENEFITS.map((benefit) => (
                <li key={benefit} className="dox-patient-benefit flex items-start gap-3 text-sm transition-colors duration-300">
                  <span className="dox-check-patient mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full transition-all duration-300">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  {benefit}
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="#consult"
                className="dox-btn-patient inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200"
              >
                Find a doctor <ArrowRight className="h-4 w-4" />
              </a>
              <a href="#faq" className="dox-link-patient text-sm font-semibold transition-colors duration-200">
                How it works →
              </a>
            </div>

            {/* ── Live Network Widget ── */}
            <NetworkWidget />
          </motion.div>

        </div>
      </div>
    </section>
  );
}